import { Stack, Tooltip, Typography } from "@mui/material";
import { GridApiPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { RatelistType as RatelistTypeEnum } from "@sinch/core/entities/serverEnums";
import { TransportDirection } from "@sinch/core/entities/serverEnums.types";
import { CalculatedRate, Ratelist, RatelistType, Scalars, WageCompositionComponent } from "@sinch/types/sinch.types";
import * as React from "react";
import { MutableRefObject, ReactNode, useEffect, useMemo } from "react";
import { RateComponentsMargin } from "@sinch/components/application/RateComponentsMarginTable/RateComponentsMargin";
import { CalculatedRateItems } from "@sinch/components/application/RatesCompositionTable/types";
import { useTransportDirection } from "@sinch/components/application/Transport/useTransportDirection";
import { flatten, pluck, prop } from "ramda";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { MdsInfo } from "@sinch/icons";
import { isNotNilOrEmpty } from "ramda-adjunct";

interface LabelGroup {
  label: string;
  ratelistType: RatelistType;
}

const getComponentLabel = (comp: Record<string, unknown>): string =>
  (typeof comp.label === "string" ? comp.label : null) ?? "Attendance";

const RATELIST_TYPE_ORDER: Record<string, number> = {
  [RatelistTypeEnum.Wage]: 0,
  [RatelistTypeEnum.Price]: 1,
  [RatelistTypeEnum.TransportWage]: 2,
  [RatelistTypeEnum.TransportPrice]: 3,
  [RatelistTypeEnum.TagWage]: 4,
  [RatelistTypeEnum.TagPrice]: 5,
};

const filterByLabel = (details: RateComponentComposition[], label: string): RateComponentComposition[] =>
  details
    .map((item) => {
      const components = item.components.filter((comp) => getComponentLabel(comp) === label);
      return {
        ...item,
        components: components,
      };
    })
    .filter((item) => item.components.length > 0);

const getLabelGroups = (...detailsArrays: RateComponentComposition[][]): LabelGroup[] => {
  const seen = new Map<string, LabelGroup>();
  for (const details of detailsArrays) {
    for (const item of details) {
      for (const comp of item.components) {
        const label = getComponentLabel(comp);
        if (!seen.has(label)) {
          seen.set(label, { label, ratelistType: comp.ratelistType });
        }
      }
    }
  }
  return Array.from(seen.values()).sort(
    (a, b) => (RATELIST_TYPE_ORDER[a.ratelistType] ?? 99) - (RATELIST_TYPE_ORDER[b.ratelistType] ?? 99)
  );
};

const flattenComposition = (details: RateComponentComposition[]) =>
  details.flatMap(({ components, total }) => ({
    total,
    components: components.flatMap(
      ({ calculatedRates, ...rest }) => calculatedRates ?? ([rest] as CalculatedRateItems["components"])
    ),
  })) as CalculatedRateItems[];

const LabelGroupTable = ({
  parentApiRef,
  priceDetails,
  wageDetails,
  hideDetails,
}: {
  parentApiRef: MutableRefObject<GridApiPro>;
  priceDetails: RateComponentComposition[];
  wageDetails: RateComponentComposition[];
  hideDetails: boolean;
}) => {
  const curApiRef = useGridApiRef();

  useEffect(() => {
    parentApiRef.current?.getAllColumns().forEach(({ field }) => {
      const col = parentApiRef.current?.getColumn(field);
      if (!col) return;
      curApiRef.current?.setColumnWidth(field, col.computedWidth);
    });

    const unsubscribe = parentApiRef.current.subscribeEvent("columnResize", ({ colDef: { field }, width }) => {
      curApiRef.current?.setColumnWidth(field, width);
    });
    return () => unsubscribe();
  }, []);

  const wageComposition = flattenComposition(wageDetails);
  const priceComposition = flattenComposition(priceDetails);

  return (
    <RateComponentsMargin
      apiRef={curApiRef as MutableRefObject<GridApiPro>}
      disableHeaders
      hideDetails={hideDetails}
      priceDetails={priceComposition}
      wageDetails={wageComposition}
    />
  );
};

export type RateComponentComposition = {
  components: (WageCompositionComponent & {
    calculatedRates?: CalculatedRate[];
    ratelistId?: string;
  })[];
  total: Scalars["Float"]["output"];
};

export const InternalRateComponentsMarginTable = ({
  parentApiRef,
  priceDetails,
  wageDetails,
  hideDetails,
}: {
  parentApiRef: MutableRefObject<GridApiPro>;
  priceDetails: RateComponentComposition[];
  wageDetails: RateComponentComposition[];
  hideDetails: boolean;
}) => {
  const { t } = useTranslation();
  const labelGroups = useMemo(() => getLabelGroups(priceDetails, wageDetails), [priceDetails, wageDetails]);
  const { getTransportDirectionLabel } = useTransportDirection();
  const getOverriddenRatelistsLabels = useOverriddenRatelistsLabels();

  const getDisplayLabel = (group: LabelGroup): string => {
    if (
      group.ratelistType === RatelistTypeEnum.TransportWage ||
      group.ratelistType === RatelistTypeEnum.TransportPrice
    ) {
      return getTransportDirectionLabel(group.label as TransportDirection);
    }
    return group.label;
  };

  return (
    <>
      {labelGroups.map((group) => {
        const priceOverridden = getOverriddenRatelistsLabels(priceDetails, group.label);

        const wageOverridden = getOverriddenRatelistsLabels(wageDetails, group.label);

        return (
          <React.Fragment key={group.label}>
            <Stack alignItems="center" direction="row" gap={0.25}>
              <Typography
                sx={(theme) => ({ px: 1, py: 0.5, mt: 1, ml: `calc((var(--row-depth, 0) + 1) * ${theme.spacing(2)})` })}
                variant="subtitle2"
              >
                {getDisplayLabel(group)}
              </Typography>
              <OverrideIcon
                priceOverrideLabel={
                  isNotNilOrEmpty(priceOverridden) && `${t("Ratelist.pricelist")}: ${priceOverridden.join(", ")}`
                }
                wageOverrideLabel={
                  isNotNilOrEmpty(wageOverridden) && `${t("Ratelist.wagelist")}: ${wageOverridden.join(", ")}`
                }
              />
            </Stack>
            <LabelGroupTable
              hideDetails={hideDetails}
              parentApiRef={parentApiRef}
              priceDetails={filterByLabel(priceDetails, group.label)}
              wageDetails={filterByLabel(wageDetails, group.label)}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

const useOverriddenRatelistsLabels = () => {
  const { t } = useTranslation();
  return (details: RateComponentComposition[], groupLabel: string) =>
    flatten(pluck("components", filterByLabel(details, groupLabel)))
      .map((item) => {
        const ratelist = prop("overridden", item) as Pick<Ratelist, "name" | "type" | "adHoc"> | undefined;
        return ratelist?.adHoc ? t("Ratelist.adHoc") : ratelist?.name;
      })
      .filter(Boolean);
};

export const OverrideIcon = ({
  wageOverrideLabel,
  priceOverrideLabel,
}: {
  wageOverrideLabel: ReactNode;
  priceOverrideLabel: ReactNode;
}) => {
  if (!wageOverrideLabel && !priceOverrideLabel) {
    return null;
  }
  return (
    <Tooltip
      title={
        <Stack>
          {isNotNilOrEmpty(priceOverrideLabel) && <span>{priceOverrideLabel}</span>}
          {isNotNilOrEmpty(wageOverrideLabel) && <span>{wageOverrideLabel}</span>}
        </Stack>
      }
    >
      <MdsIcon color="info" fontSize="xs" icon={MdsInfo} />
    </Tooltip>
  );
};
